import { createLogger } from '@utils/logger';

const log = createLogger('hereMaps');

const HERE_CDN_BASE = 'https://js.api.here.com/v3/3.1';

/**
 * The HERE JS API used to be loaded from four <script> tags in index.html. When any of them was
 * blocked, `window.H` stayed undefined and the map component crashed the whole page with
 * "Cannot read properties of undefined (reading 'service')" (RD-2824).
 *
 * js.api.here.com is on several ad-blocker / privacy-extension blocklists and is also commonly
 * blocked by corporate proxies, so a direct CDN load fails for some customers. Point
 * VITE_HERE_MAPS_BASE_URL at a same-origin reverse-proxy path (e.g. `/here-maps`) to sidestep that;
 * the CDN stays configured as a fallback, and a failed load now degrades to a placeholder instead
 * of an error page.
 */
const configuredBase = (import.meta.env.VITE_HERE_MAPS_BASE_URL as string | undefined)?.replace(/\/+$/, '');

const bases = Array.from(new Set([configuredBase, HERE_CDN_BASE].filter(Boolean))) as string[];

const inFlightAssets = new Map<string, Promise<void>>();

const loadScript = (url: string): Promise<void> => {
  const cached = inFlightAssets.get(url);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the tag so a later mount can retry (the block may be transient / proxy-side).
      script.remove();
      inFlightAssets.delete(url);
      reject(new Error(`Failed to load ${url}`));
    };
    document.head.appendChild(script);
  });

  inFlightAssets.set(url, promise);
  return promise;
};

const loadStylesheet = (url: string) => {
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = url;
  document.head.appendChild(link);
};

const loadFromBase = async (base: string) => {
  // mapsjs-core.js defines window.H; the other three augment it, so core has to land first.
  await loadScript(`${base}/mapsjs-core.js`);
  await Promise.all([
    loadScript(`${base}/mapsjs-service.js`),
    loadScript(`${base}/mapsjs-ui.js`),
    loadScript(`${base}/mapsjs-mapevents.js`),
  ]);
  loadStylesheet(`${base}/mapsjs-ui.css`);

  const H = (window as any).H;
  // A blocked request can also be answered with the SPA's index.html, which parses as a script but
  // defines nothing — so verify the API is actually there instead of trusting the load event.
  if (!H?.service?.Platform || !H?.Map || !H?.mapevents?.Behavior) {
    throw new Error(`HERE Maps scripts loaded from ${base} but window.H is incomplete`);
  }
  return H;
};

let pending: Promise<any> | null = null;

/**
 * Resolves with the HERE `H` namespace, loading the scripts on first use. Rejects when every
 * configured base fails; the failure is not cached, so a later mount retries.
 */
export const loadHereMaps = (): Promise<any> => {
  if ((window as any).H?.service?.Platform) return Promise.resolve((window as any).H);
  if (pending) return pending;

  pending = (async () => {
    let lastError: unknown;
    for (const base of bases) {
      try {
        return await loadFromBase(base);
      } catch (e) {
        lastError = e;
        log.error(`HERE Maps could not be loaded from ${base}`, e);
      }
    }
    throw lastError instanceof Error ? lastError : new Error('HERE Maps could not be loaded');
  })();

  pending.catch(() => {
    pending = null;
  });

  return pending;
};
